import { Lightning, Router, Storage } from "@lightningjs/sdk";
import { List } from "@lightningjs/ui";
import { MediaModel } from "../../../../lib/models";
import theme from "../../../../lib/theme";
import { Button } from "../../../../components/input/Button/Button";
import configs from "../../../../lib/configs";
import { STORAGE_KEYS, getMediaRedirect, getUserId, isPostTvPlatform, hasBoxPin } from "../../../../lib/utils";
import { addToWatchList, removeFromWatchList } from "../../../../api/mutations";

interface MediaActionsTemplateSpec extends Lightning.Component.TemplateSpec {
    data: MediaModel;
    Buttons: typeof List;
    hasPlay: boolean;
}

export class MediaActions
    extends Lightning.Component<MediaActionsTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<MediaActionsTemplateSpec>
{
    _boundEventHandler?: any;

    _movieId!: number | string;
    _isFree!: boolean;
    _inWatchlist = false;
    _trailerUrl: string | undefined;
    _hasPlay = false;
    _type = "";

    _movie!: MediaModel;

    static override _template(): Lightning.Component.Template<MediaActionsTemplateSpec> {
        return {
            collision: true,
            Buttons: {
                type: List,
                collision: true,
                direction: "row",
                spacing: 16,
                w: theme.layout.w
            }
        };
    }

    readonly Buttons = this.getByRef("Buttons")!;

    override _getFocused() {
        return this.Buttons;
    }

    override _setup() {
        this._boundEventHandler = this._update.bind(this);
    }

    override _active() {
        this.application.on("updateSelectedMovie", this._boundEventHandler);
    }

    override _inactive() {
        this.application.off("updateSelectedMovie", this._boundEventHandler);
    }

    override _init() {
        this._setState("WatchState");
    }

    set data(value: MediaModel) {
        this._update(value);
    }

    _update(movie: MediaModel) {
        this._setState("WatchState");

        this._movie = movie;

        if (this._movieId !== movie.id) {
            this._trailerUrl = movie.trailer?.url;
            this._movieId = movie.id;
            this._isFree = movie.isFree;
            this._inWatchlist = !!movie.inWatchList;
            this._type = movie.__typename;

            this._updateButtons();
        }
    }

    _updateButtons() {
        const items: any[] = [];

        const index = this.Buttons.index;

        if (this._hasPlay) {
            const isRestricted = this._isAgeRestricted();
            items.push({
                flexItem: { marginRight: 20 },
                w: isRestricted ? 320 : 240,
                h: theme.button.h,
                type: Button,
                hasActive: false,
                actionId: "play",
                label: isRestricted ? "actions.ageBlocked" : "actions.watch",
                // iconWidth: 20,
                icon: "play-btn",
                signals: {
                    handleClick: "_handleWatchState"
                }
            });
        }

        if (this._trailerUrl) {
            items.push({
                flexItem: { marginRight: 20 },
                w: 240,
                h: theme.button.h,
                type: Button,
                hasActive: false,
                actionId: "trailer",
                label: "general.trailer",
                // iconWidth: 20,
                icon: "trailer",
                signals: {
                    handleClick: "_handleTrailerState"
                }
            });
        }

        if (configs.modules.bookmarks) {
            items.push({
                w: 240,
                h: theme.button.h,
                type: Button,
                hasActive: false,
                label: "watchlist.toggle",
                actionId: "watchlist",
                // Use a filled-looking icon when the item IS in the watchlist,
                // and an outline/empty icon when it is NOT.
                // The visual assets have opposite semantics to their names.
                icon: this._inWatchlist ? "remove-watchlist" : "add-watchlist",
                signals: {
                    handleClick: "_handleFavoriteState"
                }
            });
        }

        if (!!items.length) {
            this.Buttons.reload(items);
            this.Buttons.setIndex(index);

            for (const wrapper of this.Buttons.children) {
                wrapper?.patch({
                    collision: true
                });
            }
        } else {
            this.Buttons.clear();
        }
    }

    $handleBtnClick(id: string) {
        if (id === "watchlist") {
            this._handleFavoriteState();
        } else if (id === "play") {
            this._handleWatchState();
        } else {
            this._handleTrailerState();
        }
    }

    async _handleWatchState() {
        if (this._isAgeRestricted()) {
            console.info("[JULIA-AgeGate] Play blocked → opening PIN verify before playback");
            const pinInput = this._getPinInputWidget();
            if (pinInput) {
                pinInput.open(
                    () => {
                        getMediaRedirect(this._movieId, {
                            isFree: this._isFree,
                            movie: this._movie
                        });
                    },
                    undefined,
                    "verify",
                    undefined,
                    4,
                    undefined,
                    true
                );
                return;
            }
            // PinInput widget unavailable — proceed without gate rather than block playback entirely
        }
        getMediaRedirect(this._movieId, {
            isFree: this._isFree,
            movie: this._movie
        });
    }

    _isAgeRestricted(): boolean {
        // Parental gate is PostTV-only (the PIN lives on the box). No box ⇒ never gate.
        if (!isPostTvPlatform()) return false;

        const fskRaw = (this._movie as any)?.fsk;
        // No rating set ⇒ treat as 16+ (legislation).
        const fsk = typeof fskRaw === "number" ? fskRaw : 16;
        const user = Storage.get(STORAGE_KEYS.user) as any;
        const restrictionRaw =
            user?.settings?.parentalAgeRestriction ||
            (Storage.get(STORAGE_KEYS.parentalAgeRestriction) as string | undefined);
        // No age level chosen yet (fresh install) ⇒ default to 12+ (Sky youth-protection spec).
        const restriction = restrictionRaw ? parseInt(restrictionRaw as string, 10) : 12;
        // The PIN lives on the box, never in our backend — we never read
        // user.settings.parentalPin. hasBoxPin(): Android = window.Android.testPinCode
        // present; Linux = pics.pin synced to Storage. Without a box PIN we can't unlock,
        // so fall through (don't gate) rather than dead-end the user.
        const hasPin = hasBoxPin();

        const restricted =
            fsk > 0 &&
            !isNaN(restriction) &&
            hasPin &&
            fsk > restriction;

        console.info(
            "[JULIA-AgeGate] _isAgeRestricted",
            JSON.stringify({ movieId: this._movieId, fskRaw, fsk, restrictionRaw, restriction, hasPin, restricted })
        );
        return restricted;
    }

    _getPinInputWidget() {
        let current: any = this;
        let depth = 0;
        while (current && depth < 20) {
            try {
                if (current.getByRef) {
                    const widgets = current.getByRef("Widgets");
                    if (widgets) {
                        const pinInput = widgets.getByRef("PinInput");
                        if (pinInput) return pinInput;
                    }
                }
            } catch (e) {
                // continue traversing
            }
            current = current.parent;
            depth++;
            if (!current || current === this.application || current === this.stage) {
                break;
            }
        }
        return null;
    }

    async _handleTrailerState() {
        Router.navigate(`trailer`);
    }

    async _handleFavoriteState() {
        // this._setState("FavoriteState");

        // this._handleFavoriteState();

        if (this._movieId && getUserId()) {
            try {
                if (this._inWatchlist) {
                    await removeFromWatchList(this._movieId!, this._type);
                } else {
                    await addToWatchList(this._movieId!, this._type);
                }
                this._inWatchlist = !this._inWatchlist;
                // window.selectedMovie!.inWatchList = this._inWatchlist;

                this._updateButtons();
            } catch (e) {
                console.log("e", e);
            }
        } else {
            if (isPostTvPlatform()) {
                Router.focusWidget("PostTvSubscription");
            } else {
                Router.focusWidget("Auth");
            }
        }
    }

    set hasPlay(value: boolean) {
        this._hasPlay = value;
    }
}
